import { AANAccountType, ISelectedAccount, TransactionTypesEnum } from '@interfaces/models';
import { OnboardingInitializationBody } from '@interfaces/open-choice';
import {
  ClientDetailsRequestBody,
  ClientEligibilityBody,
} from '@interfaces/open-choice/digital-advice.types';
import { createSelector } from '@ngrx/store';
import * as dayjs from 'dayjs';
import {
  selectClientMailingAddresses,
  selectRetirementAge,
  selectTaxableIncome,
  selectTaxStatus,
} from 'vg-interaction-model';

import { getOpenChoiceState, State } from '../../reducers/index';
import {
  getAccountId,
  getDAAccountsToEnroll,
  getDAComponentFailure,
  getdraftId,
  getEnrollPageHTML,
  getOnboardingInitialized,
  getPortfolioMatchResult,
} from '../../reducers/open-choice/open-choice.reducer';
import { selectOpenAccountResponse } from '../open-account/open-account.selectors';
import { selectClientDateOfBirth } from '../personal-data/personal-data.selectors';
import {
  selectedAccountsFrom,
  selectedAccountTo,
  selectTransactionType,
} from '../selected-client-accounts/selected-client-accounts.selectors';

export const selectOpenChoiceState = createSelector(getOpenChoiceState, (state) => state);

export const selectAcceptedDAOffer = createSelector(
  getOpenChoiceState,
  (state) => state?.acceptedDAOffer || false,
);

export const selectGotDAOffer = createSelector(
  getOpenChoiceState,
  (state) => state?.gotDAOffer || false,
);

export const selectDAComponentFailure = createSelector(getOpenChoiceState, getDAComponentFailure);

export const selectOnboardingInitialized = createSelector(
  getOpenChoiceState,
  getOnboardingInitialized,
);

export const selectPortfolioMatchResult = createSelector(
  selectOpenChoiceState,
  getPortfolioMatchResult,
);

export const selectDAAccountsToEnroll = createSelector(
  selectOpenChoiceState,
  getDAAccountsToEnroll,
);

export const selectAccountId = createSelector(selectOpenChoiceState, getAccountId);

export const selectEnrollDraftID = createSelector(selectOpenChoiceState, getdraftId);

export const selectEnrollPageHTML = createSelector(selectOpenChoiceState, getEnrollPageHTML);

export const selectDAClientEligibilityBody = createSelector<
  State,
  ISelectedAccount | null,
  ISelectedAccount[] | null,
  TransactionTypesEnum | null,
  ClientEligibilityBody
>(
  selectedAccountTo,
  selectedAccountsFrom,
  selectTransactionType,
  (toAccount, fromAccounts, transactionType) => {
    const balance = parseFloat(fromAccounts?.[0]?.accountData.balance || '0');
    switch (transactionType) {
      case TransactionTypesEnum.EXISTING: {
        return {
          newAccounts: [],
          existingAccounts: [toAccount?.accountData.accountId || ''],
        };
      }
      case TransactionTypesEnum.NEW_ACCOUNT:
      case TransactionTypesEnum.FORCED_NEW_ACCOUNT: {
        return {
          newAccounts: [
            {
              accountId: 'NEW_ACCOUNT',
              balance,
              // We can only open new individual accounts
              accountType: AANAccountType.INDIVIDUAL,
            },
          ],
          existingAccounts: [],
        };
      }
      case TransactionTypesEnum.UNKNOWN:
      default: {
        return {
          newAccounts: [],
          existingAccounts: [],
        };
      }
    }
  },
);

const formatDateOfBirth = (raw: string | null): string => dayjs(raw).format('YYYY-MM-DD');

export const selectOnboardingInitRequestBody = createSelector(
  selectClientDateOfBirth,
  selectTaxStatus,
  selectRetirementAge,
  selectClientMailingAddresses,
  selectTaxableIncome,
  selectTransactionType,
  selectedAccountsFrom,
  selectedAccountTo,
  (
    dateOfBirth,
    taxFilingStatus,
    retirementAge,
    addresses,
    taxableIncome,
    transactionType,
    fromAccounts,
    toAccount,
  ) => {
    const balance = parseFloat(fromAccounts?.[0]?.accountData.balance || '0');
    const withNewAccounts: OnboardingInitializationBody['withNewAccounts'] = [];
    switch (transactionType) {
      case TransactionTypesEnum.NEW_ACCOUNT:
      case TransactionTypesEnum.FORCED_NEW_ACCOUNT:
        withNewAccounts.push({
          accountType: AANAccountType.INDIVIDUAL,
          balance,
          id: 'NEW_ACCOUNT',
        });
        break;
      case TransactionTypesEnum.EXISTING:
        withNewAccounts.push({
          accountType: toAccount?.accountData.accountType || '',
          balance,
          id: toAccount?.accountData.accountId || '',
        });
        break;
    }
    const body: OnboardingInitializationBody = {
      dateOfBirth: formatDateOfBirth(dateOfBirth),
      taxFilingStatus,
      retirementAge,
      stateTaxReportingCode: addresses?.[0]?.state,
      description: 'CWP Open Choice',
      highTaxBracketPreference: !!taxableIncome?.exceeds,
      withNewAccounts,
    };
    return body;
  },
);
export const selectRequestBodyForOnboardingInitialization = createSelector(
  selectTaxStatus,
  selectRetirementAge,
  selectAccountId,
  selectEnrollDraftID,
  selectDAAccountsToEnroll,
  selectOpenAccountResponse,
  selectTransactionType,
  (
    taxStatus,
    retirementAge,
    openChoiceAccountId,
    draftID,
    accounts,
    openAccountResponse,
    transactionType,
  ): ClientDetailsRequestBody => {
    const enrollMultipleAccounts: boolean =
      (accounts && accounts.placeholderAccounts.length > 1) || false;

    return {
      taxFilingStatus: taxStatus,
      retirementAge: retirementAge,
      accountId:
        transactionType !== TransactionTypesEnum.EXISTING
          ? openAccountResponse?.accountId
          : openChoiceAccountId,
      confirmationNumber: openAccountResponse?.confirmationNumber,
      draftEpiId: draftID || '',
      enrollMultipleAccounts,
      enrollNewAccount: transactionType !== TransactionTypesEnum.EXISTING,
    };
  },
);
